import {useQuery} from "@tanstack/react-query";
import {createCurrencyRepository} from "../data/repositories";
import {FALLBACK_CURRENCIES} from "../domain/currency.types";

const CURRENCIES_QUERY_KEY = ["currencies"] as const;
const STALE_TIME_MS = 1000 * 60 * 30; // 30 minutes

export interface UseCurrenciesResult {
  currencies: string[];
  isLoading: boolean;
  error: Error | null;
  isUsingFallback: boolean;
}

/**
 * Fetches currencies via the currency repository.
 * Never blocks rendering — returns fallback immediately on error.
 */
export function useCurrencies(): UseCurrenciesResult {
  const repository = createCurrencyRepository();

  const query = useQuery({
    queryKey: CURRENCIES_QUERY_KEY,
    queryFn: () => repository.getCurrencies(),
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });

  const data = query.data;
  const currencies = query.isLoading
    ? [...FALLBACK_CURRENCIES]
    : data?.currencies ?? [...FALLBACK_CURRENCIES];
  const isUsingFallback = data?.fromFallback ?? false;

  return {
    currencies,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    isUsingFallback,
  };
}
